<script>
import { clickoutside } from "@svelte-put/clickoutside";
import {
    playerState,
    updatePlayerSpeed,
} from "@app/store/playerStore.svelte.js";
import { adrState } from "@app/store/adrStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import tcLib from "@cclib/lib/timecode.js";
import { 
    audioScrub
} from "@app/store/audioScrubStore.svelte.js";
import * as m from '@app/paraglide/messages.js';

let showRateMenu = $state(false);
let showVolumeControl = $state(false);
let volume = $state(1);
let tc = $state("00:00:00:00");
let previousMediaPath = $state(undefined);

function validateTc(e) {
    tc = tcLib.tcValidate(
        e.target.value,
        projectState.frameRate,
        projectState.dropFrame
    );

    player.currentTime = tcLib.tcToSec(tc, projectState.frameRate, projectState.dropFrame).toFixed(3) - projectState.incode;
}

function frameByFrame(e) {
    if (e.key === "ArrowUp") {
        player.currentTime += 1 / projectState.frameRate;
        audioScrub();
    } else if (e.key === "ArrowDown") {
        player.currentTime -= 1 / projectState.frameRate;
        audioScrub();
    }
}

function updateTc(sec) {
    tc = tcLib.secToTc(
        sec + projectState.incode,
        projectState.frameRate,
        projectState.dropFrame
    );
}

function updatePlaybackSpeed(speed) {
    player.playbackRate = speed;
    updatePlayerSpeed(speed);
    showRateMenu = false;
}

function goToStart() {
    player.currentTime = 0;
}

function goToEnd() {
    player.currentTime = player.duration;
}

function rewind() {
    player.currentTime = playerState.time - (1 / projectState.frameRate);
    audioScrub();
}

function fastForward() {
    player.currentTime = playerState.time + 1 / projectState.frameRate;
    audioScrub();
}

async function togglePlayback() {
    try {
        if (player.state.playing) {
            await player.pause();
        } else {
            await player.play();
        }
    } catch (err) {
        console.log(err.message);
    }
}

function prevEvent() {
    try {
        eventGroupState[projectState.selected].selected = [
            eventGroupState[projectState.selected].selected[0] - 1,
        ];
        
        updatePlayerTime();
        
        const selectedIndex = eventGroupState[projectState.selected].selected[0];
        
        // Dispatch custom event to scroll to the selected event
        window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
            detail: { index: selectedIndex }
        }));
    } catch (err) {
        console.log(err, err.message);
    }
}

function nextEvent() {
    try {
        if (
            eventGroupState[projectState.selected].selected[0] + 1 <
            eventGroupState[projectState.selected].events.length
        ) {
            eventGroupState[projectState.selected].selected = [
                eventGroupState[projectState.selected].selected[0] + 1,
            ];
            updatePlayerTime();

            const selectedIndex = eventGroupState[projectState.selected].selected[0];
            
            // Dispatch custom event to scroll to the selected event
            window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                detail: { index: selectedIndex }
            }));
        }
    } catch (err) {
        console.log(err, err.message);
    }
}

function updatePlayerTime() {
    let index =
        eventGroupState[projectState.selected].selected[0] || parseInt("0");
    if (
        lockState.video &&
        !isNaN(eventGroupState[projectState.selected].events[index].start) &&
        player
    ) {
        player.currentTime =
            eventGroupState[projectState.selected].events[index].start;
    }
}

$effect(() => {
    if (projectState.frameRate || projectState.incode) {
        updateTc(playerState.time);
    }
});

$effect(() => {
    // console.log("EFFECT Syncing player volume");
    try {
        if (player) {
            player.volume = volume;
        }
    } catch (err) {
        console.log(err.message);
    }
});

$effect(() => {
    const mediaPath = projectState.media?.path;
    if (mediaPath !== previousMediaPath) {
        previousMediaPath = mediaPath;
        if (mediaPath) {
            updatePlayerSpeed(1);
            if (player) player.playbackRate = 1;
        }
    }
});

$effect(() => {
    try {
        if (player) {
            player.playbackRate = playerState.speed;
        }
    } catch (err) {
        console.log(err.message);
    }
});

</script>

<div
    class="btn-group btn-group-sm w-100 shadow"
    role="group"
    id="PlayerControls">
    <div
        class="dropdown {showVolumeControl ? 'show' : ''}"
        use:clickoutside
        onclickoutside={() => (showVolumeControl = false)}>
        <button
            id="VolumeDropdown"
            class="btn btn-sm btn-light dropdown-toggle rounded-0"
            type="button"
            onclick={() => (showVolumeControl = true)}>
            {#if volume > 0.7}
                <i class="bi bi-volume-up"></i>
            {:else if volume === 0}
                <i class="bi bi-volume-mute"></i>
            {:else if volume < 0.3}
                <i class="bi bi-volume-off"></i>
            {:else}
                <i class="bi bi-volume-down"></i>
            {/if}
        </button>
        <div class="dropdown-menu p-2 {showVolumeControl ? 'show' : ''}">
            <i class="bi bi-music-note-beamed"></i>
            <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                bind:value={volume} />
            {#if eventGroupState[projectState.selected] && eventGroupState[projectState.selected].type === "audio description"}
                <i class="bi bi-badge-ad"></i>
                <input
                    id="AdVolumeControl"
                    type="range"
                    min="0"
                    max="1"
                    step="0.025"
                    bind:value={playerState.adVolume} />
            {/if}
        </div>
    </div>
    <div
        class="dropdown {showRateMenu ? 'show' : ''}"
        use:clickoutside
        onclickoutside={() => (showRateMenu = false)}>
        <button
            id="RateDropdown"
            class="btn btn-sm btn-light dropdown-toggle rounded-0"
            type="button"
            onclick={() => (showRateMenu = true)}>x{playerState.speed}</button>
        <div class="dropdown-menu {showRateMenu ? 'show' : ''}">
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(0.25)}>0.25</a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(0.5)}>0.50</a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(0.75)}>0.75</a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(1.0)}>1.00</a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(1.25)}>1.25</a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(1.5)}>1.50</a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(1.75)}>1.75</a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(2.0)}>2.00</a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(4.0)}>4.00</a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(8.0)}>8.00</a>
            <a
                class="dropdown-item"
                href="#!/"
                onclick={() => updatePlaybackSpeed(16.0)}>16.0</a>
        </div>
    </div>

    <button
        aria-label={m.player_go_to_start_aria_label()}
        onclick={goToStart}
        class="btn btn-sm btn-light"
        type="button"
        title={m.player_go_to_start_title()}>
        <i class="bi bi-skip-start-fill"></i>
    </button>

    <button
        aria-label={m.player_frame_back_aria_label()}
        onclick={rewind}
        class="btn btn-sm btn-light"
        type="button"
        title={m.player_frame_back_title()}>
        <i class="bi bi-skip-backward-fill"></i></button>
    <!-- RECORD START -->
    {#if adrState.enable && adrState.selectedOutput}
        <button
            aria-label={m.player_record_toggle_aria_label()}
            onclick={() => (adrState.record = !adrState.record)}
            class="btn btn-sm {adrState.record ? 'btn-danger' : 'btn-light '}"
            type="button"
            title={m.player_record_toggle_title()}>
            <i class="bi bi-record-fill"></i>
        </button>
    {/if}
    <!-- RECORD END -->
    <button
        id="ccc-play-button"
        onclick={() => togglePlayback()}
        class="btn btn-sm {playerState.playing ? 'btn-primary' : 'btn-light'}"
        type="button"
        title={m.player_play_pause_title()}>
        {#if playerState.playing}
            <i class="bi bi-pause-fill"></i>
        {:else}
            <i class="bi bi-play-fill"></i>
        {/if}
    </button>
    <button
        onclick={fastForward}
        class="btn btn-sm btn-light"
        type="button"
        title={m.player_frame_forward_title()}
        aria-label={m.player_frame_forward_aria_label()}>
        <i class="bi bi-skip-forward-fill"></i></button>
    <button
        aria-label={m.player_go_to_end_aria_label()}
        onclick={goToEnd}
        class="btn btn-sm btn-light"
        type="button"
        title={m.player_go_to_end_title()}>
        <i class="bi bi-skip-end-fill"></i>
    </button>

    <!-- TC Reader -->
    <input
        type="text"
        class="text-center bg-light"
        style="width: 12ch; font-size: 0.8vw; border: none"
        onkeydown={frameByFrame}
        bind:value={tc}
        onblur={validateTc}
        onkeyup={(e) => e.key === "Enter" && validateTc(e)}
        onfocus={(e) => {
            e.target.select();
        }} />

    <button
        aria-label={m.player_previous_event_aria_label()}
        class="btn btn-sm btn-light"
        type="button"
        title={m.player_previous_event_title()}
        onclick={prevEvent}
        disabled={!eventGroupState[projectState.selected] ||
            eventGroupState[projectState.selected].selected.length === 0 ||
            eventGroupState[projectState.selected].selected[0] === 0}>
        <i class="bi bi-chevron-left"></i>
    </button>

    <button
        aria-label={m.player_next_event_aria_label()}
        class="btn btn-light"
        type="button"
        title={m.player_next_event_title()}
        onclick={nextEvent}
        disabled={!eventGroupState[projectState.selected] ||
            eventGroupState[projectState.selected].selected.length === 0 ||
            eventGroupState[projectState.selected].selected[0] + 1 ===
                eventGroupState[projectState.selected].events.length}>
        <i class="bi bi-chevron-right"></i>
    </button>

    <!-- LOCKS -->
    <button
        aria-label={m.player_caption_lock_aria_label()}
        class="btn btn-sm {lockState.caption ? 'btn-primary' : 'btn-light'}"
        type="button"
        title={m.player_caption_lock_title()}
        onclick={() => (lockState.caption = !lockState.caption)}>
        <i
            class="bi {eventGroupState[projectState.selected] &&
            eventGroupState[projectState.selected].type === 'audio description'
                ? 'bi-badge-ad'
                : 'bi-badge-cc'}"></i>
        <sub
            ><i
                class="bi {lockState.caption
                    ? 'bi-lock-fill'
                    : 'bi-unlock-fill'}"></i
            ></sub
        ></button>

    <button
        aria-label={m.player_video_lock_aria_label()}
        class="btn btn-sm {lockState.video ? 'btn-primary' : 'btn-light'}"
        type="button"
        title={m.player_video_lock_title()}
        onclick={() => (lockState.video = !lockState.video)}>
        <i class="bi bi-film"></i>
        <sub
            ><i class="bi {lockState.video ? 'bi-lock-fill' : 'bi-unlock-fill'}"
            ></i
            ></sub
        ></button>

    <button
        aria-label={m.player_preview_lock_aria_label()}
        class="btn btn-sm rounded-0 {lockState.preview
            ? 'btn-primary'
            : 'btn-light'}"
        type="button"
        title={m.player_preview_lock_title()}
        onclick={() => (lockState.preview = !lockState.preview)}>
        <i class="bi {lockState.preview ? 'bi-eye-fill' : 'bi-eye-slash'}"></i>
    </button>
</div>

<style lang="css">
.dropdown-menu {
    z-index: 9999;
}

i {
    pointer-events: none;
    font-size: 0.8vw;
}
</style>
